import { zodResolver } from '@hookform/resolvers/zod';
import { WandSparkles } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';
import useTrainersNotesGeneration from '../hooks/useTrainersNotesGeneration';
import {
    useSubmitTrainersNotesResultMutation,
    useUpdateTrainersNotesResultMutation,
    useAssessmentResultsQuery,
} from './TraineeReports.api';
import { TrainersNotesStatus, type AssessmentResultFilterRule } from '@/apolloClient.types';
import Button from '@/components/common/Button';
import { EditorField } from '@/components/fields/EditorField';
import { useGetCompanyId } from '@/contexts/UserSession';
import { success as showSuccessNotification, error as showErrorNotification } from '@/utils/notifications/actions';

interface TrainersNotesEditorProps {
    assessmentRunId: string;
    userId: string; // trainee id
    onBack: () => void;
}

const TrainersNotesEditor = ({ assessmentRunId, userId, onBack }: TrainersNotesEditorProps) => {
    const { t } = useTranslation('overallTraineeReport');
    const [resultId, setResultId] = useState<string | null>(null);
    const [isPublished, setIsPublished] = useState(false);
    const companyId = useGetCompanyId();

    const formSchema = useMemo(() => z.object({ trainersNotes: z.string().optional() }), []);
    const form = useForm<{ trainersNotes?: string }>({
        mode: 'onChange',
        resolver: zodResolver(formSchema),
        defaultValues: { trainersNotes: '' },
    });
    const notesValue = form.watch('trainersNotes') || '';

    const filter = useMemo(
        (): AssessmentResultFilterRule => ({
            assessmentRunIds: [assessmentRunId],
            userIds: [userId],
        }),
        [assessmentRunId, userId]
    );

    const { data, refetch } = useAssessmentResultsQuery({
        fetchPolicy: 'cache-and-network',
        variables: { filter },
    });

    useEffect(() => {
        const item = data?.assessmentResults?.items?.find(i => i.result.__typename === 'TrainersNotesResult');
        if (item) {
            setResultId(item.id);
            const r = item.result.__typename === 'TrainersNotesResult' ? item.result.trainersNotes || '' : '';
            form.setValue('trainersNotes', r || '');
            setIsPublished(
                item.result.__typename === 'TrainersNotesResult'
                    ? item.result.status === TrainersNotesStatus.Published
                    : false
            );
        }
    }, [data?.assessmentResults?.items, form]);

    const [updateMutation] = useUpdateTrainersNotesResultMutation();
    const [submitMutation] = useSubmitTrainersNotesResultMutation();

    // Use subscription-based generation hook
    const { isGenerating, generate: handleAskAI } = useTrainersNotesGeneration({
        assessmentRunId,
        userId,
        companyId: companyId || '',
        onSuccess: suggestion => {
            form.setValue('trainersNotes', suggestion);
            showSuccessNotification({
                message: t('sections.trainerNotes.suggestionReadyTitle'),
                description: t('sections.trainerNotes.suggestionReadyDescription'),
            });
        },
        onError: errorMessage => {
            showErrorNotification({
                message: t('error.generateError'),
                description: errorMessage || t('error.unknown'),
            });
        },
    });

    const handleSaveDraft = useCallback(async () => {
        if (!companyId) {
            return;
        }

        try {
            const notes = form.getValues().trainersNotes || '';
            if (resultId) {
                await updateMutation({
                    variables: { id: resultId, input: { companyId, trainersNotes: notes, toBePublished: false } },
                });
            } else {
                await submitMutation({
                    variables: {
                        input: { userId, companyId, assessmentRunId, trainersNotes: notes, toBePublished: false },
                    },
                });
            }
            await refetch();
            showSuccessNotification({
                message: t('sections.trainerNotes.saveDraftTitle'),
                description: t('sections.trainerNotes.saveDraftDescription'),
            });
        } catch (err: any) {
            showErrorNotification({
                message: t('error.saveDraftError'),
                description: err?.message || t('error.unknown'),
            });
        }
    }, [companyId, resultId, submitMutation, updateMutation, refetch, t, userId, assessmentRunId, form]);

    const handlePublish = useCallback(async () => {
        if (!companyId) {
            return;
        }

        try {
            const notes = form.getValues().trainersNotes || '';
            if (resultId) {
                await updateMutation({
                    variables: { id: resultId, input: { companyId, trainersNotes: notes, toBePublished: true } },
                });
            } else {
                await submitMutation({
                    variables: {
                        input: { userId, companyId, assessmentRunId, trainersNotes: notes, toBePublished: true },
                    },
                });
            }
            await refetch();
            setIsPublished(true);
            showSuccessNotification({
                message: t('sections.trainerNotes.publishTitle'),
                description: t('sections.trainerNotes.publishDescription'),
            });
        } catch (err: any) {
            showErrorNotification({
                message: t('error.publishError'),
                description: err?.message || t('error.unknown'),
            });
        }
    }, [companyId, userId, assessmentRunId, resultId, refetch, submitMutation, updateMutation, t, form]);

    const isDisabled = !notesValue.trim() || isPublished;

    return (
        <FormProvider {...form}>
            <div className="relative py-6">
                <div className="font-semibold">{t('sections.trainerNotes.title')}</div>
                <div className="mt-3">
                    <EditorField
                        name="trainersNotes"
                        placeholder={t('sections.trainerNotes.placeholder')}
                        disabled={isPublished}
                    />
                </div>
                <div className="mt-4 flex items-center justify-between">
                    <Button color="lightTeal" onClick={handleAskAI} disabled={isPublished || isGenerating}>
                        <div className="flex items-center gap-2">
                            {isGenerating ? (
                                <>
                                    <span className="size-4 animate-spin rounded-full bg-[conic-gradient(#5b21b6,#1e40af,#be123c,#5b21b6)] [mask:radial-gradient(farthest-side,transparent_55%,#000_56%)]" />
                                    <span className="animate-rainbow-move bg-gradient-to-r from-violet-800 via-blue-800 to-rose-800 bg-[length:300%_100%] bg-clip-text text-transparent">
                                        {t('actions.generatingAiTrainerSuggestion')}
                                    </span>
                                </>
                            ) : (
                                <>
                                    <WandSparkles className="size-4" />
                                    {t('actions.askAITrainer')}
                                </>
                            )}
                        </div>
                    </Button>
                    <div className="flex items-center gap-2">
                        <Button outline onClick={onBack}>
                            {t('actions.cancel')}
                        </Button>
                        <Button
                            outline
                            onClick={handleSaveDraft}
                            color={isDisabled ? 'disabled' : 'default'}
                            disabled={isDisabled}
                        >
                            {t('actions.saveDraft')}
                        </Button>
                        <Button onClick={handlePublish} color={isDisabled ? 'disabled' : 'teal'} disabled={isDisabled}>
                            {t('actions.publish')}
                        </Button>
                    </div>
                </div>
            </div>
        </FormProvider>
    );
};

export default TrainersNotesEditor;
